import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ShieldCheck } from "lucide-react";
import { sponsorApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Sponsor Admin invitation acceptance — public route at
 * /sponsor-invite/:token. If the invited email already has a LifeGuide
 * account they enter their existing password; otherwise they set a new
 * password and we mint an account (backend enforces).
 */
export default function SponsorInviteAcceptPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState(null);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    sponsorApi
      .peekInvite(token)
      .then(setInvite)
      .catch((e) => setErr(e?.response?.data?.detail || "Invitation not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await sponsorApi.acceptInvite(token, { password, name });
      toast.success("Invitation accepted");
      // Log the user in with the password they just used so they land in
      // the app as a real signed-in sponsor admin.
      try {
        // Best-effort: hit /auth/login using their email + password
        const backend = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${backend}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: invite.email, password }),
        });
        if (r.ok) {
          const data = await r.json();
          localStorage.setItem("lg_token", data.token);
          await refresh();
        }
      } catch {
        // fall through — user will just need to sign in manually
      }
      nav(`/sponsor-dashboard/${res.sponsor_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invitation");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#7C3E33]" data-testid="invite-error">
        {err}
      </div>
    );
  }
  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[#2E2E2E]" data-testid="sponsor-invite-page">
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center">
              <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
          </Link>
        </div>
      </header>

      <section className="max-w-lg mx-auto px-6 pt-16 pb-16">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#E1ECDD] border border-[#4F6F52]/20 px-4 py-1.5 mb-6">
          <ShieldCheck size={13} className="text-[#4F6F52]" />
          <span className="text-xs uppercase tracking-[0.16em] text-[#4F6F52] font-semibold">
            Sponsor Admin invitation
          </span>
        </div>
        <h1 className="font-display text-3xl mb-3">
          You're invited to manage{" "}
          <span className="text-[#4F6F52]">{invite.sponsor_name}</span>
        </h1>
        <p className="text-[#4E5A4C] leading-relaxed mb-8">
          As a Sponsor Admin, you'll see aggregate analytics for your
          organization's LifeGuide impact — never individual user activity,
          journal entries, or private content.
        </p>

        <form
          onSubmit={submit}
          data-testid="invite-form"
          className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-4"
        >
          <div>
            <div className="text-xs text-[#6B6B6B] mb-1.5">Email</div>
            <div className="rounded-2xl bg-[#F5F7F2] border border-[#DCE5DB] px-4 py-2.5 text-[#4E5A4C]">
              {invite.email}
            </div>
          </div>
          {!invite.existing_account && (
            <label className="block">
              <div className="text-xs text-[#6B6B6B] mb-1.5">Your name (optional)</div>
              <input
                data-testid="invite-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3"
              />
            </label>
          )}
          <label className="block">
            <div className="text-xs text-[#6B6B6B] mb-1.5">
              {invite.existing_account ? "Your LifeGuide password" : "Set a password"}
            </div>
            <input
              data-testid="invite-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3"
              placeholder={invite.existing_account ? "Your existing password" : "At least 6 characters"}
            />
          </label>
          <button
            data-testid="invite-accept"
            type="submit"
            disabled={busy}
            className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3.5 font-medium disabled:opacity-60"
          >
            {busy ? "Accepting…" : "Accept invitation"}
          </button>
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            Sponsor Admins can only see their own organization's aggregate
            analytics. LifeGuide never shares individual user names, emails,
            journal entries, or private guidance conversations with any
            sponsor.
          </p>
        </form>
      </section>
    </div>
  );
}
